/**
 * Device Authorization Service for Child App
 * Handles device activation codes and authorization status via Backend API
 */

/**
 * Generate a unique device ID for this browser/device
 */
function generateDeviceId() {
  // Try to get existing device ID from localStorage
  let deviceId = localStorage.getItem('kassie_device_id');

  if (!deviceId) {
    // Generate a truly unique device ID using crypto.randomUUID()
    // This ensures each browser profile gets a unique ID
    if (crypto.randomUUID) {
      deviceId = crypto.randomUUID();
    } else {
      // Fallback for older browsers
      deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }

    localStorage.setItem('kassie_device_id', deviceId);
    console.log('🆕 Generated new device ID:', deviceId);
  }

  return deviceId;
}

/**
 * Check if current device is authorized via Backend API
 */
export async function checkDeviceAuthorization() {
  try {
    const deviceId = generateDeviceId();
    console.log('🔍 Checking device authorization via backend API for device:', deviceId);

    // Import API service
    const { api } = await import('./api.js');

    // Get stored family ID for authorization check
    const storedFamily = localStorage.getItem('authorizedFamily');
    if (!storedFamily) {
      console.log('❌ No stored family found for authorization check');
      return {
        isAuthorized: false,
        deviceId,
        familyId: null,
        authorizedAt: null
      };
    }

    const family = JSON.parse(storedFamily);

    // Check authorization via backend API
    const response = await api.get('/api/auth/device/check', {
      device_id: deviceId,
      family_id: family.id
    });

    console.log('✅ Device authorization response:', response);

    return {
      isAuthorized: response.authorized || false,
      deviceId,
      familyId: response.familyId || family.id,
      authorizedAt: new Date(),
      authorizationId: null
    };

  } catch (error) {
    console.error('❌ Error checking device authorization via API:', error);
    console.log('🔄 Falling back to localStorage check');

    // Fallback: check localStorage for stored authorization
    const storedAuth = localStorage.getItem('deviceAuthorization');
    const storedFamily = localStorage.getItem('authorizedFamily');

    if (storedAuth && storedFamily) {
      return {
        isAuthorized: true,
        deviceId: generateDeviceId(),
        familyId: JSON.parse(storedFamily).id,
        authorizedAt: new Date(),
        fallbackMode: true
      };
    }

    return {
      isAuthorized: false,
      deviceId: generateDeviceId(),
      familyId: null,
      authorizedAt: null,
      error: error.message,
      fallbackMode: true
    };
  }
}

/**
 * Validate activation code and authorize device via Backend API
 */
export async function validateActivationCode(code) {
  try {
    const deviceId = generateDeviceId();
    console.log('🔑 Validating activation code via backend API:', code);

    // Import API service
    const { api } = await import('./api.js');

    // Validate code via backend API
    const response = await api.post('/api/auth/device/activation-code/validate', {
      code: code,
      device_id: deviceId
    });

    console.log('✅ Activation code validation response:', response);

    if (response.success) {
      return {
        success: true,
        familyId: response.familyId,
        deviceId: deviceId,
        family: response.family
      };
    } else {
      return {
        success: false,
        error: response.error || 'Ongeldige kode. Probeer weer.'
      };
    }

  } catch (error) {
    console.error('❌ Error validating activation code via API:', error);
    return {
      success: false,
      error: 'Iets het verkeerd geloop. Probeer weer.'
    };
  }
}

/**
 * Get device ID for current device
 */
export function getDeviceId() {
  return generateDeviceId();
}
